import { useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Copy, FolderTree, Package, ListChecks, Layers } from "lucide-react";
import { toast } from "sonner";
import { LineDialog } from "@/components/targets/LineDialog";

const KIND_META = {
  catalog: ["Ürün Gamı", FolderTree, "text-amber-300"],
  product: ["Tek Ürün", Package, "text-blue-300"],
  products: ["Toplu Ürün", ListChecks, "text-violet-300"],
};

const err = (e, m) => toast.error(e?.response?.data?.detail || m);

function LineRow({ l, groups, isVirtual, onEdit, onDelete, onMove }) {
  const [kl, Icon, cls] = KIND_META[l.kind];
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-800/80 hover:bg-slate-800/20 transition-colors" data-testid={`setup-line-${l.id}`}>
      <Icon size={16} className={cls} />
      <div className="flex-1 min-w-[160px]">
        <div className="font-semibold text-sm">{l.name}</div>
        <div className="text-[11px] text-slate-500">{l.label ? `${l.source_name} · ` : ""}{l.sub}</div>
        {l.overlap_with && <div className="text-[11px] text-red-300 mt-0.5" data-testid={`setup-line-overlap-${l.id}`}>Uyarı: bazı ürünler "{l.overlap_with}" satırında da sayılıyor</div>}
      </div>
      <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{kl}</Badge>
      {isVirtual && groups.length > 0 && (
        <Select onValueChange={(gid) => onMove(l, gid)}>
          <SelectTrigger className="h-8 w-40 bg-slate-950/50 border-slate-700 text-xs" data-testid={`setup-line-move-${l.id}`}><SelectValue placeholder="Gruba taşı" /></SelectTrigger>
          <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Button size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`setup-line-edit-${l.id}`}><Pencil size={14} /></Button>
      <Button size="sm" variant="ghost" onClick={() => onDelete(l)} data-testid={`setup-line-delete-${l.id}`}><Trash2 size={14} className="text-red-400" /></Button>
    </div>
  );
}

function GroupCard({ g, realGroups, onChanged, openLine }) {
  const [name, setName] = useState(g.name);
  const update = async (data) => {
    try { await http.put(`/target-groups/${g.id}`, data); onChanged(); } catch (e) { err(e, "Güncellenemedi"); }
  };
  const remove = async () => {
    if (!window.confirm(`"${g.name}" grubu ve içindeki tüm satırlar (verilen hedeflerle birlikte) silinsin mi?`)) return;
    try { await http.delete(`/target-groups/${g.id}`); toast.success("Grup silindi"); onChanged(); } catch (e) { err(e, "Silinemedi"); }
  };
  const delLine = async (l) => {
    if (!window.confirm(`"${l.name}" satırı silinsin mi? Bu satıra verilen şube hedefleri de kaldırılır.`)) return;
    try { await http.delete(`/target-items/${l.id}`); toast.success("Satır silindi"); onChanged(); } catch (e) { err(e, "Silinemedi"); }
  };
  const move = async (l, gid) => {
    try { await http.put(`/target-items/${l.id}`, { group_id: gid, order: 99 }); onChanged(); } catch (e) { err(e, "Taşınamadı"); }
  };
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass overflow-hidden" data-testid={`setup-group-${g.id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-900/80">
        <Layers size={16} className="text-blue-400" />
        {g.virtual ? (
          <div className="flex-1 font-display font-bold">{g.name} <span className="text-[10px] font-mono text-amber-300 ml-2">GRUPSUZ</span></div>
        ) : (
          <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== g.name && update({ name: name.trim() })}
                 className="flex-1 min-w-[140px] h-9 bg-transparent border-transparent hover:border-slate-700 focus:border-slate-600 font-display font-bold text-base" data-testid={`setup-group-name-${g.id}`} />
        )}
        {!g.virtual && (
          <>
            <Select value={g.hgo_mode} onValueChange={(v) => update({ hgo_mode: v })}>
              <SelectTrigger className="h-9 w-44 bg-slate-950/50 border-slate-700 text-xs" data-testid={`setup-group-mode-${g.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Birleşik HGO (toplam)</SelectItem>
                <SelectItem value="per_line">Satır bazlı HGO</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="border-slate-700 h-9" onClick={() => openLine(g.id)} data-testid={`setup-group-add-line-${g.id}`}><Plus size={14} className="mr-1" /> Satır</Button>
            <Button size="sm" variant="ghost" className="h-9" onClick={remove} data-testid={`setup-group-delete-${g.id}`}><Trash2 size={14} className="text-red-400" /></Button>
          </>
        )}
      </div>
      {g.lines.map((l) => (
        <LineRow key={l.id} l={l} groups={realGroups} isVirtual={g.virtual} onEdit={(x) => openLine(x.group_id, x)} onDelete={delLine} onMove={move} />
      ))}
      {g.lines.length === 0 && <div className="px-4 py-4 text-xs text-slate-500 border-t border-slate-800/80">Bu grupta satır yok. "Satır" ile ekleyin.</div>}
    </Card>
  );
}

export function TargetSettings({ year, month, setup, products, catalogs, onChanged }) {
  const [newName, setNewName] = useState("");
  const [lineCtx, setLineCtx] = useState(null);
  const realGroups = setup.filter((g) => !g.virtual);

  const [busy, setBusy] = useState(false);
  const addGroup = async (e) => {
    e?.preventDefault();
    if (!newName.trim()) { toast.error("Önce grup adını yazın"); return; }
    setBusy(true);
    try {
      await http.post("/target-groups", { year, month, name: newName.trim(), hgo_mode: "combined", order: realGroups.length });
      toast.success(`"${newName.trim()}" grubu eklendi`);
      setNewName(""); onChanged();
    } catch (er) { err(er, "Grup eklenemedi"); } finally { setBusy(false); }
  };
  const copyPrev = async () => {
    try {
      const { data } = await http.post("/target-items/copy-previous", { year, month });
      toast.success(`${data.source}: ${data.groups} grup, ${data.added} satır kopyalandı`);
      onChanged();
    } catch (e) { err(e, "Kopyalanamadı"); }
  };
  const saveLine = async (body) => {
    const { groupId, line } = lineCtx;
    try {
      if (line?.id) await http.put(`/target-items/${line.id}`, body);
      else await http.post("/target-items", { ...body, year, month, group_id: groupId, order: 99 });
      toast.success("Satır kaydedildi");
      setLineCtx(null); onChanged();
    } catch (e) { err(e, "Kaydedilemedi"); }
  };

  return (
    <div className="space-y-4" data-testid="target-settings">
      <Card className="border-slate-800 bg-slate-900/60 glass">
        <CardContent className="p-4">
          <form onSubmit={addGroup} className="flex flex-wrap gap-2 items-center">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder="Yeni grup adı (örn: Mobil, Dsl, Tivibu)" className="flex-1 min-w-[200px] bg-slate-950/50 border-slate-700" data-testid="setup-new-group-name" />
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="setup-add-group"><Plus size={15} className="mr-1" /> Grup Ekle</Button>
          {setup.length === 0 && (
            <Button type="button" variant="outline" className="border-slate-700" onClick={copyPrev} data-testid="setup-copy-prev"><Copy size={15} className="mr-1" /> Önceki Aydan Kopyala</Button>
          )}
          </form>
        </CardContent>
      </Card>
      {setup.map((g) => (
        <GroupCard key={g.id} g={g} realGroups={realGroups} onChanged={onChanged} openLine={(groupId, line) => setLineCtx({ groupId, line })} />
      ))}
      <LineDialog open={!!lineCtx} initial={lineCtx?.line} products={products} catalogs={catalogs}
                  onClose={() => setLineCtx(null)} onSave={saveLine} />
    </div>
  );
}
